"use client";

import { useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import { Loader2, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useLocale } from "next-intl";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/context/ToastContext";

interface Question {
  id: string;
  question: string;
  answer: string | null;
  helpfulCount: number;
  createdAt: string;
  answeredAt: string | null;
  askedBy: string;
}

export default function ProductQuestions({ productId }: { productId: string }) {
  const { toast } = useToast();
  const locale = useLocale();
  const { data: session } = useSession();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Form state
  const [questionText, setQuestionText] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await fetch(`/api/products/${productId}/questions`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setQuestions(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const submitQuestion = async () => {
    if (!questionText.trim() || questionText.trim().length < 5) {
      toast("Please write a question (at least 5 characters)", "error");
      return;
    }
    if (!session?.user && !guestName.trim()) {
      toast("Please enter your name", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/products/${productId}/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: questionText,
          guestName: !session?.user ? guestName : undefined,
          guestEmail: !session?.user ? guestEmail : undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed");
      }
      toast(
        "Question submitted — we'll respond shortly",
        "success"
      );
      setQuestionText("");
      setGuestName("");
      setGuestEmail("");
      setShowForm(false);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {questions.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-brand-dark/10 rounded-sm">
          <MessageCircle className="h-8 w-8 text-brand-muted/30 mx-auto mb-3" />
          <p className="text-sm text-brand-dark mb-1">No questions yet</p>
          <p className="text-xs text-brand-muted">Be the first to ask!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => {
            const isExpanded = expanded.has(q.id);
            return (
              <div
                key={q.id}
                className="border border-brand-dark/8 rounded-sm bg-white overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(q.id)) next.delete(q.id);
                      else next.add(q.id);
                      return next;
                    })
                  }
                  className="w-full flex items-start justify-between gap-3 p-4 text-start hover:bg-brand-cream/20 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-brand-gold uppercase tracking-wider mb-1">
                      Q
                    </p>
                    <p className="text-sm text-brand-dark font-medium">
                      {q.question}
                    </p>
                    <p className="text-[11px] text-brand-muted mt-1">
                      Asked by {q.askedBy} ·{" "}
                      {new Date(q.createdAt).toLocaleDateString(locale)}
                    </p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-brand-muted shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-brand-muted shrink-0" />
                  )}
                </button>
                {isExpanded && q.answer && (
                  <div className="border-t border-brand-dark/8 bg-brand-cream/20 p-4">
                    <p className="text-xs font-medium text-brand-gold uppercase tracking-wider mb-1">
                      A
                    </p>
                    <p className="text-sm text-brand-dark/80 whitespace-pre-line">
                      {q.answer}
                    </p>
                    <p className="text-[11px] text-brand-muted mt-2">
                      Rivky Wigs ·{" "}
                      {q.answeredAt
                        ? new Date(q.answeredAt).toLocaleDateString(locale)
                        : ""}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!showForm ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowForm(true)}
        >
          <MessageCircle className="h-4 w-4" />
          Ask a Question
        </Button>
      ) : (
        <div className="border border-brand-dark/8 rounded-sm bg-brand-cream/20 p-5">
          <h4 className="font-display text-base text-brand-dark mb-3">
            Ask a Question
          </h4>
          {!session?.user && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[10px] font-medium text-brand-dark mb-1 uppercase tracking-wider">
                  Your Name
                </label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  maxLength={100}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-brand-dark mb-1 uppercase tracking-wider">
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  maxLength={200}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold"
                />
              </div>
            </div>
          )}
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="What would you like to know?"
            rows={3}
            maxLength={2000}
            className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold resize-none"
          />
          <div className="flex items-center gap-2 mt-3 justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={submitQuestion}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
